import {useState, type ReactNode} from 'react';
import {Button, DropdownMenuCheckboxItem, Input} from '@kroll-resolver-crisp/ui-components-react';

export type MultiSelectOption = {value: string; label: string};

// Above this many options, a checklist alone makes finding one entry tedious (scrolling a list of
// ~200 languages/topics/locations to find one) - the search box earns its space once the list is
// long enough that typing beats scanning; below it, the box is just unused chrome.
const SEARCHABLE_THRESHOLD = 8;

// The menu's own typeahead/arrow-key navigation listens on the popup - stop it from eating
// keystrokes meant for an input inside it, but let Escape still bubble up to close the menu.
export function stopMenuTypeahead(event: React.KeyboardEvent) {
  if (event.key !== 'Escape') {
    event.stopPropagation();
  }
}

function CheckOption({
  checked,
  onToggle,
  children
}: {
  readonly checked: boolean;
  readonly onToggle: () => void;
  readonly children: ReactNode;
}) {
  // The menu's own checkbox item rather than a label wrapped round a Checkbox: inside a menu the
  // arrow keys and typeahead reach a menu item, where a bare label was only ever clickable.
  return (
    <DropdownMenuCheckboxItem checked={checked} onCheckedChange={onToggle}>
      {children}
    </DropdownMenuCheckboxItem>
  );
}

type MultiSelectFilterBodyProps = {
  readonly label: string;
  readonly options: MultiSelectOption[];
  readonly selected: string[];
  readonly onChange: (next: string[]) => void;
  // When set, `options` is treated as already server-filtered for the current search term (e.g. a
  // language search reaching the full ISO 639-3 set) - the search box stays visible regardless of how
  // few results come back, and this fires on every keystroke instead of only filtering client-side.
  readonly onSearchChange?: (term: string) => void;
  readonly emptyLabel?: string;
};

export function MultiSelectFilterBody({
  label,
  options,
  selected,
  onChange,
  onSearchChange,
  emptyLabel = 'No matches.'
}: MultiSelectFilterBodyProps) {
  const [search, setSearch] = useState('');
  const searchable = Boolean(onSearchChange) || options.length > SEARCHABLE_THRESHOLD;
  const visibleOptions = onSearchChange
    ? options
    : searchable
      ? options.filter((option) => option.label.toLowerCase().includes(search.trim().toLowerCase()))
      : options;

  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  }

  return (
    <div className="space-y-1">
      {searchable ? (
        <Input
          value={search}
          placeholder={`Search ${label.toLowerCase()}...`}
          className="h-7"
          onChange={(event) => {
            setSearch(event.target.value);
            onSearchChange?.(event.target.value);
          }}
          onKeyDown={stopMenuTypeahead}
        />
      ) : null}
      {visibleOptions.length === 0 ? (
        <p className="text-muted-foreground px-1.5 py-1 text-xs">{emptyLabel}</p>
      ) : (
        <div className="max-h-56 overflow-y-auto">
          {visibleOptions.map((option) => (
            <CheckOption
              key={option.value}
              checked={selected.includes(option.value)}
              onToggle={() => {
                toggle(option.value);
              }}>
              <span className="truncate">{option.label}</span>
            </CheckOption>
          ))}
        </div>
      )}
      <MenuClearButton
        disabled={selected.length === 0}
        onClear={() => {
          onChange([]);
        }}
      />
    </div>
  );
}

// The ghost "Clear" every multi-select menu ends with. A button rather than a menu item so it never
// closes the menu, and disabled rather than hidden so the menu does not change height as you pick.
export function MenuClearButton({
  disabled,
  onClear
}: {
  readonly disabled: boolean;
  readonly onClear: () => void;
}) {
  return (
    <Button variant="ghost" size="sm" className="w-full" disabled={disabled} onClick={onClear}>
      Clear
    </Button>
  );
}
